import React, { useState, useEffect } from "react";
import { Link, useNavigate } from 'react-router-dom';
import { Dropdown } from 'react-bootstrap';
import team3 from 'assets/img/team/avatar.png';
import Avatar from 'components/common/avatar';
import paths from 'routes/paths';
import { toast } from "react-toastify";
import axios from "axios";
import { useTranslation } from "react-i18next"; // Import useTranslation hook
import { useAppContext } from "../../../providers/appProvider";
import { USER_ROLES } from "config/roles.config";
import { disconnectSocket } from "assets/js/common";

const ProfileDropdown = () => {
  const { t, i18n } = useTranslation(); // Access translation function
  const [data, setData] = useState([]);
  const navigate = useNavigate(); // Initialize the useNavigate hook
  const [loading, setLoading] = useState(false);
  const [roles, setRoles] = useState([]);
  const [adminRoleId, setAdminRoleId] = useState(null);
  const [appOwnerRoleId, setAppOwnerRoleId] = useState(null);
  const { setLocations, changeLocation } = useAppContext();
  const locationId = localStorage.getItem("LocationId");
  const userId = localStorage.getItem("UserId");
  const isClientUser = localStorage.getItem("UserRole") === USER_ROLES.CLIENT;

  const fetchUserData = async (userId) => {
    try {
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL}/userRoute/getUserById/${userId}`, {
        headers: {
          Authorization: process.env.REACT_APP_BasicAuthSecret
        }
      }
      );
      setData(response.data);
      setLoading(false);
    } catch (error) {
      console.error("Error fetching user data:", error);
      setLoading(false);
    }
  };

  const fetchRoles = async () => {
    try {
      const response = await axios.get(`${process.env.REACT_APP_API_URL}/roleRoute/roles`, {
        headers: {
          Authorization: process.env.REACT_APP_BasicAuthSecret
        }
      }
      );
      setRoles(response.data);

      const adminRole = response.data.find((roles) => roles.Name === USER_ROLES.ADMIN);
      const appOwnerRole = response.data.find((roles) => roles.Name === USER_ROLES.APP_OWNER);

      setAdminRoleId(adminRole?.RoleId || null);
      setAppOwnerRoleId(appOwnerRole?.RoleId || null);
    } catch (error) {
      console.error('Error fetching roles:', error);
    }
  };

  useEffect(() => {
    fetchUserData(userId);
    fetchRoles();
  }, [userId]);

  const getColorFromName = (name) => {
    const colors = [
      "bg-primary-subtle text-primary",
      "bg-success-subtle text-success",
      "bg-warning-subtle text-warning",
      "bg-danger-subtle text-danger",
      "bg-info-subtle text-info",
    ];

    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }

    return colors[Math.abs(hash) % colors.length];
  };

  const SALUTATIONS = ["mr", "mrs", "ms", "dr",];

  const getInitials = (name) => {
      if (!name) return "U";
      let words = name
        .trim()
        .split(" ")
        .filter((word) => word.length > 0);

      if (words.length > 1 && SALUTATIONS.includes(words[0].toLowerCase().replace(".", ""))) {
        words = words.slice(1);
      }

      return words
        .map((word) => word.charAt(0))
        .join("")
        .substring(0, 2)
        .toUpperCase();
    };

  const handleLogout = () => {
    const updatedFormData = { ...data, userId, skipAudit: true };
    if (updatedFormData?.Role === adminRoleId || updatedFormData?.Role === appOwnerRoleId) {
      // Update user table with LastLoginLocation
      updatedFormData.LastLoginLocation = locationId;
      axios.patch(`${process.env.REACT_APP_API_URL}/userRoute/updateUser/${userId}`, updatedFormData, {
        headers: {
          Authorization: process.env.REACT_APP_BasicAuthSecret
        }
      }
      );
    } else {
      // If roleId does not match, update with null
      axios.patch(`${process.env.REACT_APP_API_URL}/userRoute/updateUser/${userId}`, updatedFormData, {
        headers: {
          Authorization: process.env.REACT_APP_BasicAuthSecret
        }
      }
      );
    }

    toast.success(t("logoutSucessMessage"), {
      position: "bottom-left",
      autoClose: 5000,
    });

    // Preserve app update metadata
    const appVersion = localStorage.getItem('app_version');
    const lastForcedVersion = localStorage.getItem('last_forced_logout_version');
    const ordersFilterOption = localStorage.getItem('ordersFilterOption');

    disconnectSocket();

    // Clear user session
    sessionStorage.clear();
    localStorage.clear();

    // Restore app update metadata
    if (appVersion) {
      localStorage.setItem('app_version', appVersion);
    }
    if (lastForcedVersion) {
      localStorage.setItem('last_forced_logout_version', lastForcedVersion);
    }
    if (ordersFilterOption) {
      localStorage.setItem('ordersFilterOption', ordersFilterOption);
    }

    setLocations([]);
    changeLocation(null);

    // Navigate to login page and prevent going back by using replace
    navigate(`${paths.login}`, { replace: true });
  };

  return (
    <Dropdown navbar={true} as="li">
      <Dropdown.Toggle
        bsPrefix="toggle"
        as="div"
        to="#"
        className="pe-0 ps-2 nav-link"
      >
        {(() => {
          const name = data?.Name || data?.Username || "User";
          const colorClass = getColorFromName(name);

          return (
            <div className={`avatar avatar-xl rounded-circle ${colorClass} d-flex align-items-center justify-content-center`}>
              <span className="fw-bold">
                {getInitials(name)}
              </span>
            </div>
          );
        })()}
      </Dropdown.Toggle>

      <Dropdown.Menu className="dropdown-caret dropdown-menu-card dropdown-menu-end">
        <div className="bg-white rounded-2 py-2 dark__bg-1000">
              <Dropdown.Item onClick={() => navigate(`${paths.profile}`)}>
                <i className="fas fa-user me-2"></i>
                {(data.Name || data.Username || 'User').charAt(0).toUpperCase() + (data.Name || data.Username || 'User').slice(1)}
              </Dropdown.Item>
              <Dropdown.Divider />
              {!isClientUser && (
                <>
                  <Dropdown.Item onClick={() => navigate(`${paths.mySubscription}`)}>
                    <i className="fas fa-sync me-2"></i>
                  {t("mySubscription")}
                  </Dropdown.Item>
                  <Dropdown.Divider />
                </>
              )}
          <Dropdown.Item onClick={handleLogout}>
            <i className="fas fa-sign-out-alt me-2"></i>
            {t("logout")}
          </Dropdown.Item>
        </div>
      </Dropdown.Menu>
    </Dropdown>
  );
};

export default ProfileDropdown;